import React, { useState, useEffect } from 'react';
import Button from '../../NecttosComp/Button/Button';
import { Body, Header, Main } from '../../NecttosComp/Layout/Layout';
import { getAxiosTokenInstance } from '../../utils/axiosInstance';
import { Table, Tbody, Td, Th, Thead, Tr, useSortableData } from '../../NecttosComp/Table/Table';
import toast from 'react-hot-toast';
import Instruction from '../../Documentation/Instruction';
import Input2 from '../../NecttosComp/Input/Input2';

interface CareerData {
	_id: string;
	fullName?: string;
	email?: string;
	phoneNumber?: string;
	status?: 'Pending' | 'Contacted' | 'Approved' | 'Rejected';
	createdAt?: string;
	[key: string]: any;
}

interface CareerViewProps {
	onClose: () => void;
}

const CareerView: React.FC<CareerViewProps> = ({ onClose }) => {
	const [careerData, setCareerData] = useState<CareerData[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [selectedCareer, setSelectedCareer] = useState<CareerData | null>(null);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState('');

	const fetchCareerData = async () => {
		setLoading(true);
		setError(null);
		try {
			const instance = await getAxiosTokenInstance();
			const response = await instance.get('/website/careers');
			if (response.data && response.data.data) {
				setCareerData(response.data.data);
			} else {
				setCareerData([]);
			}
		} catch (err: any) {
			console.error(err);
			const errorMessage = err?.response?.data?.message || 'Failed to fetch career data';
			setError(errorMessage);
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchCareerData();
	}, []);

	const openModal = (career: CareerData) => {
		setSelectedCareer(career);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setSelectedCareer(null);
		setIsModalOpen(false);
	};

	const handleStatusChange = async (career: CareerData, e: React.MouseEvent) => {
		e.stopPropagation();

		const statusOrder: ('Pending' | 'Contacted' | 'Approved' | 'Rejected')[] = ['Pending', 'Contacted', 'Approved', 'Rejected'];
		const currentStatus = career.status || 'Pending';
		const currentIndex = statusOrder.indexOf(currentStatus);
		const nextIndex = (currentIndex + 1) % statusOrder.length;
		const newStatus = statusOrder[nextIndex];

		try {
			const instance = await getAxiosTokenInstance();
			await instance.put(`/website/careers/${career._id}`, { status: newStatus });

			setCareerData((prev) => prev.map((c) => (c._id === career._id ? { ...c, status: newStatus } : c)));

			toast.success(`Status changed to ${newStatus}`);
		} catch (err: any) {
			const msg = err?.response?.data?.message || 'Failed to update status';
			toast.error(msg);
		}
	};

	const getStatusButtonStyle = (status: string) => {
		switch (status) {
			case 'Approved':
				return 'bg-green-500 hover:bg-green-600 text-white';
			case 'Rejected':
				return 'bg-red-500 hover:bg-red-600 text-white';
			case 'Contacted':
				return 'bg-blue-500 hover:bg-blue-600 text-white';
			case 'Pending':
			default:
				return 'bg-yellow-500 hover:bg-yellow-600 text-white';
		}
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return '-';
		try {
			const date = new Date(dateString);
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			});
		} catch {
			return '-';
		}
	};

	const Modal: React.FC<{ onClose: () => void; career: CareerData }> = ({ onClose, career }) => {
		return (
			<div className='fixed inset-0 bg-gray-900 bg-opacity-[50%] flex justify-center items-center z-50'>
				<div className='bg-slate-100 dark:bg-neutral-800 dark:text-white rounded-2xl p-10 w-full max-w-4xl shadow-2xl relative'>
					<h2 className='text-2xl font-semibold text-center text-gray-800 dark:text-white mb-4'>Career Application Details</h2>
					<div className='grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-700 dark:text-white'>
						<div>
							<span className='font-medium'>Name:</span> {career.fullName || '-'}
						</div>
						<div>
							<span className='font-medium'>Email:</span> {career.email || '-'}
						</div>
						<div>
							<span className='font-medium'>Phone Number:</span> {career.phoneNumber || '-'}
						</div>
						<div>
							<span className='font-medium'>Applied Date:</span> {formatDate(career.createdAt)}
						</div>
						{Object.entries(career).map(([key, value]) => {
							if (['_id', 'fullName', 'email', 'phoneNumber', '__v', 'createdAt', 'status'].includes(key)) return null;
							const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
							return (
								<div key={key}>
									<span className='font-medium'>{label}:</span> {String(value) || '-'}
								</div>
							);
						})}

						<div className='sm:col-span-2'>
							<label className='block text-sm font-medium text-gray-700 dark:text-white mb-1'>Status</label>
							<select
								className='w-full border border-gray-300 bg-slate-100 dark:bg-neutral-800 rounded-md px-3 py-2 text-sm'
								value={career.status || 'Pending'}
								onChange={async (e) => {
									const newStatus = e.target.value as 'Pending' | 'Contacted' | 'Approved' | 'Rejected';

									try {
										const instance = await getAxiosTokenInstance();
										await instance.put(`/website/careers/${career._id}`, { status: newStatus });

										setCareerData((prev) => prev.map((c) => (c._id === career._id ? { ...c, status: newStatus } : c)));
										setSelectedCareer((prev) => (prev ? { ...prev, status: newStatus } : prev));

										toast.success('Status updated successfully');
									} catch (err: any) {
										const msg = err?.response?.data?.message || 'Failed to update status';
										toast.error(msg);
									}
								}}>
								<option value='Pending'>Pending</option>
								<option value='Contacted'>Contacted</option>
								<option value='Approved'>Approved</option>
								<option value='Rejected'>Rejected</option>
							</select>
						</div>
					</div>
					<div className='mt-6 flex justify-end'>
						<Button type='close' onClick={onClose}>
							Close
						</Button>
					</div>
				</div>
			</div>
		);
	};

	const filteredCareerData = careerData.filter((career) => {
		const lower = searchTerm.toLowerCase();
		return career.fullName?.toLowerCase().includes(lower) || career.phoneNumber?.toLowerCase().includes(lower) || career.status?.toLowerCase().includes(lower);
	});

	const { tableItems, requestSort, getIcon } = useSortableData(filteredCareerData || []);

	return (
		<Main title='View Career Registrations' width='100%' height='100%'>
			<Header>
				<div className='flex'>
					<Input2 type='text' fieldName='Search' state={searchTerm} setState={setSearchTerm} className='w-[300px] h-[40px] border-0' />
				</div>
				<div className='flex'>
					<Button type='print'>Print</Button>
					<Button type='excel'>Excel</Button>
					<Instruction keyId={'CareerView'} />
					<Button type='close' onClick={onClose}>
						Close
					</Button>
				</div>
			</Header>

			<Body>
				<div className='overflow-x-auto max-h-100'>
					<Table width='100%' innerWidth={['5%', '20%', '20%', '20%', '15%']}>
						<Thead>
							<Tr>
								<Th position={0}>Sl.No</Th>
								<Th position={1} onClick={() => requestSort('fullName')} icon={getIcon('fullName')}>
									Name
								</Th>
								<Th position={2} onClick={() => requestSort('email')} icon={getIcon('email')}>
									Email
								</Th>
								<Th position={3} onClick={() => requestSort('phoneNumber')} icon={getIcon('phoneNumber')}>
									Phone Number
								</Th>
								<Th position={4} onClick={() => requestSort('status')} icon={getIcon('status')}>
									Status
								</Th>
							</Tr>
						</Thead>
						<Tbody height='auto'>
							{tableItems.map((career, i) => (
								<Tr key={career._id} onClick={() => openModal(career)} height='auto'>
									<Td index={i} position={0}>
										{i + 1}
									</Td>
									<Td index={i} position={1}>
										{career.fullName || '-'}
									</Td>
									<Td index={i} position={2}>
										{career.email || '-'}
									</Td>
									<Td index={i} position={3}>
										{career.phoneNumber || '-'}
									</Td>
									<Td index={i} position={4}>
										<button onClick={(e) => handleStatusChange(career, e)} className={`px-3 py-1 text-xs rounded-md shadow-md font-medium transition-colors duration-200 ${getStatusButtonStyle(career.status || 'Pending')}`}>
											{career.status || 'Pending'}
										</button>
									</Td>
								</Tr>
							))}
						</Tbody>
					</Table>
				</div>

				{isModalOpen && selectedCareer && <Modal onClose={closeModal} career={selectedCareer} />}
			</Body>
		</Main>
	);
};

export default CareerView;
